/**
 * Class responsible for managing the UI components of the Bill of Materials (BOM) table.
 * @category Components
 * @subcategory BOM
 */
export class BomTableUI {
  /**
   * Creates an instance of BomTableUI.
   * @param {Object} csvDownloader - An instance of a CSV downloader utility.
   */
  constructor(csvDownloader) {
    this.csvDownloader = csvDownloader;
  }


  cleanup() {
    console.log("BomTableUI cleanup started...");
  
    // 1. Remove modal from DOM
    if (this.modalContainer && document.body.contains(this.modalContainer)) {
      document.body.removeChild(this.modalContainer);
      this.modalContainer = null;
    }
  
    // 2. Remove event listeners from buttons
    if (this.closeModalBtn) {
      this.closeModalBtn.onclick = null;
      this.closeModalBtn = null;
    }
  
    if (this.downloadButton) {
      this.downloadButton.onclick = null;
      this.downloadButton = null;
    }
  
    // 3. Clear table contents
    const tbody = document.getElementById("bomTableBody");
    if (tbody) tbody.innerHTML = "";
  
    const footer = document.getElementById("bomTableFooter");
    if (footer) footer.innerHTML = "";
  
    // 4. Dispose utility instances
    if (this.csvDownloader && typeof this.csvDownloader.dispose === "function") {
      this.csvDownloader.dispose();
    }
    this.csvDownloader = null;
  
    // 5. Nullify object references
    this.bomTableBody = null;
    this.bomTableFooter = null;
  
    console.log("BomTableUI cleanup completed.");
  }
  /**
   * Creates the modal container for the BOM table.
   * @returns {HTMLDivElement} The modal container element.
   */
  createModal() {
    const modalContainer = document.createElement("div");
    modalContainer.className = "bomTable-container";
    modalContainer.id = "bomTable-modal";

    const draggableContainer = document.createElement("div");
    draggableContainer.className = "bomTable-draggable-container";

    // Title block centered
    const modalTitleBlock = this.createModalTitleBlock();
    draggableContainer.appendChild(modalTitleBlock);

    const table = this.createTable();
    draggableContainer.appendChild(table);

    const actionContainer = this.createActionContainer();
    draggableContainer.appendChild(actionContainer);

    const fillinContainer = this.createDraggableFillinContainer();
    draggableContainer.appendChild(fillinContainer);

    const resizeContainer = this.createResizeContainer();
    modalContainer.appendChild(resizeContainer);

    modalContainer.appendChild(draggableContainer);

    return modalContainer;
  }

  /**
   * Creates the title block for the modal.
   * @returns {HTMLDivElement} The title block element.
   */
  createModalTitleBlock() {
    const titleBlock = document.createElement("div");
    titleBlock.className = "bomTable-title-block";
    const modalLabel = document.createElement("h2");
    modalLabel.className = "bomTable-modal-label";
    modalLabel.textContent = "Bill of Materials";
    const closeModalBtn = document.createElement("button");
    closeModalBtn.className = "bomTable-close-modal";
    closeModalBtn.innerHTML = "&times;";
    closeModalBtn.onclick = () => {
      const bomModal = document.getElementById("bomTable-modal");
      bomModal.style.display = "none";

      const bomSwitch = document.getElementById("bom-switch");
      bomSwitch.checked = false;
    };
    titleBlock.appendChild(modalLabel);
    titleBlock.appendChild(closeModalBtn);
    return titleBlock;
  }

  /**
   * Creates the BOM table element.
   * @returns {HTMLTableElement} The table element.
   */
  createTable() {
    const table = document.createElement("table");
    table.className = "bomTable-part-number-table";
    table.id = "bomTable";
    const header = this.createTableHeader([
      "ID",
      "Part #",
      "QTY",
      "Each",
      "Total",
      "Note",
    ]);
    table.appendChild(header);
    const tbody = document.createElement("tbody");
    tbody.id = "bomTableBody";
    table.appendChild(tbody);
    const tfoot = this.createFooter();
    table.appendChild(tfoot);
    return table;
  }

  /**
   * Creates the header for the BOM table.
   * @param {string[]} headers - Array of header names.
   * @returns {HTMLTableSectionElement} The table header element.
   */
  createTableHeader(headers) {
    const thead = document.createElement("thead");
    const tr = document.createElement("tr");
    headers.forEach((header) => {
      const th = document.createElement("th");
      th.textContent = header;
      tr.appendChild(th);
    });
    thead.appendChild(tr);
    return thead;
  }

  /**
   * Creates the footer for the BOM table.
   * @returns {HTMLTableSectionElement} The table footer element.
   */
  createFooter() {
    const tfoot = document.createElement("tfoot");
    tfoot.id = "bomTableFooter";
    return tfoot;
  }

  /**
   * Creates the action container for the modal, including the download button and alert.
   * @returns {HTMLDivElement} The action container element.
   */
  createActionContainer() {
    const actionContainer = document.createElement("div");
    actionContainer.className = "bomTable-action-container";
    const downloadButton = this.createDownloadButton();
    actionContainer.appendChild(downloadButton);
    const alertDiv = this.createAlertDiv();
    actionContainer.appendChild(alertDiv);
    return actionContainer;
  }

  /**
   * Creates the download button for the modal.
   * @returns {HTMLButtonElement} The download button element.
   */
  createDownloadButton() {
    const button = document.createElement("button");
    button.id = "bomTable-download-button";
    button.classList.add("bomTableDownloadCsv");
    button.textContent = "Download CSV";
    button.onclick = () => this.csvDownloader.downloadCSV();
    return button;
  }

  /**
   * Creates an alert div for displaying no components message.
   * @returns {HTMLDivElement} The alert div element.
   */
  createAlertDiv() {
    const alertDiv = document.createElement("div");
    alertDiv.id = "bomTable-bom-alert";
    alertDiv.textContent = "No components added";
    alertDiv.style.display = "none";
    return alertDiv;
  }

  /**
   * Creates a fill-in container for draggable modal behavior.
   * @returns {HTMLDivElement} The draggable fill-in container element.
   */
  createDraggableFillinContainer() {
    const fillinContainer = document.createElement("div");
    fillinContainer.className = "fillin-container";
    fillinContainer.style.cssText = `
    flex-grow: 1;
    width: 100%;
    height: 100%;
  `;
    return fillinContainer;
  }

  /**
   * Creates a resize container for the modal footer.
   * @returns {HTMLDivElement} The resize container element.
   */
  createResizeContainer() {
    // Add the resize container
    const resizeContainer = document.createElement("div");
    resizeContainer.className = "bomTable-resize-footer";
    resizeContainer.style.cssText = `
    position: absolute;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 10px;
    cursor: ns-resize;
  `;
    return resizeContainer;
  }

  /**
   * Updates the BOM table with new data.
   * @param {Object} bomData - The BOM data to populate the table.
   * @param {Array<Object>} bomData.data - Array of table row data.
   */
  updateTable(bomData) {
    const tbody = document.getElementById("bomTableBody");
    tbody.innerHTML = "";
    bomData.data.forEach((item) => {
      const tr = document.createElement("tr");
      ["id", "partNumber", "qty", "each", "total", "note"].forEach((key) => {
        const td = document.createElement("td");
        if (key === "each" || key === "total") {
          td.textContent = this.formatCurrency(item[key]);
        } else {
          td.textContent = item[key];
        }
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
  }

  /**
   * Updates the footer of the BOM table with totals.
   * @param {Array} total - Array containing total summary, value, and note.
   */
  updateFooter(total) {
    const footer = document.getElementById("bomTableFooter");
    footer.innerHTML = "";

    if (total) {
      const tr = document.createElement("tr");
      tr.style.textAlign = "right";

      // Create the first cell that spans the first 4 columns
      const tdSummary = document.createElement("td");
      tdSummary.colSpan = 4;
      tdSummary.textContent = total[0];

      // Second cell for the null or zero value
      const tdValue = document.createElement("td");
      tdValue.textContent = this.formatCurrency(total[1]);

      // Third cell for the note or additional text
      const tdNote = document.createElement("td");
      tdNote.textContent = total[2];

      // Append cells to the row
      tr.appendChild(tdSummary);
      tr.appendChild(tdValue);
      tr.appendChild(tdNote);

      // Append the row to the footer
      footer.appendChild(tr);
    }
  }

  /**
   * Clears the BOM table content.
   */
  clearTable() {
    const tbody = document.getElementById("bomTableBody");
    tbody.innerHTML = "";
    const footer = document.getElementById("bomTableFooter");
    footer.innerHTML = "";
  }

  /**
   * Formats a value as currency.
   * @param {number} value - The value to format.
   * @returns {string} The formatted currency string, or "NA" for invalid values.
   */
  formatCurrency(value) {
    // Check for null, undefined, NaN, or zero values
    if (value === null || value === undefined || isNaN(value) || value === 0) {
      return "NA";
    }
    // Format positive numbers as currency
    return `$${parseFloat(value).toFixed(2)}`;
  }
}
